import React, { useEffect, useRef, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Push2FAPrompt
 *
 * Shows the user-facing prompt: "Open ValuePlus App and enter NN to approve".
 * Polls /api/2fa/push/challenge/{token} every 2s and calls onApproved/onDenied/onExpired.
 *
 * Props:
 *  - challengeToken (string, required)
 *  - numberCode (string, required) — the 2-digit number to display
 *  - ttlSeconds (number, default 90)
 *  - onApproved (fn)
 *  - onDenied (fn) — user denied OR wrong number on app side
 *  - onExpired (fn)
 *  - onCancel (fn) — user clicks cancel
 *  - recoveryCodeMode (bool) — when true, shows "Use recovery code" input below
 *  - onRecoveryCode (fn) — receives the entered code
 */
export default function Push2FAPrompt({
  challengeToken,
  numberCode,
  ttlSeconds = 90,
  onApproved,
  onDenied,
  onExpired,
  onCancel,
  recoveryCodeMode = false,
  onRecoveryCode,
}) {
  const [remaining, setRemaining] = useState(ttlSeconds);
  const [showRecovery, setShowRecovery] = useState(false);
  const [recovery, setRecovery] = useState('');
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      try {
        const res = await fetch(`${API}/2fa/push/challenge/${challengeToken}`);
        if (!res.ok) return;
        const data = await res.json();
        if (stopped) return;
        setRemaining(data.remaining_seconds || 0);
        if (data.status === 'APPROVED') {
          stopped = true;
          clearInterval(pollIntervalRef.current);
          onApproved?.();
        } else if (data.status === 'DENIED') {
          stopped = true;
          clearInterval(pollIntervalRef.current);
          onDenied?.();
        } else if (data.status === 'EXPIRED' || data.remaining_seconds === 0) {
          stopped = true;
          clearInterval(pollIntervalRef.current);
          onExpired?.();
        }
      } catch {
        // ignore transient errors
      }
    };
    tick();
    pollIntervalRef.current = setInterval(tick, 2000);
    return () => {
      stopped = true;
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [challengeToken, onApproved, onDenied, onExpired]);

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;

  return (
    <div className="space-y-5" data-testid="push-2fa-prompt">
      <div className="text-center">
        <div
          className="inline-flex items-center justify-center mb-4 rounded-full"
          style={{
            width: 80,
            height: 80,
            background: 'linear-gradient(145deg, #C5A059, #B0904F)',
            boxShadow: '0 10px 30px rgba(197,160,89,0.35)',
          }}
        >
          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-primary">Bestätigung in der App</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Öffne die ValuePlus App auf deinem Handy und tippe die folgende Zahl ein:
        </p>
      </div>

      <div
        className="mx-auto text-center font-mono font-bold py-6 rounded-xl"
        style={{
          fontSize: '4.5rem',
          letterSpacing: '0.5rem',
          background: 'linear-gradient(180deg, #FFF8EA 0%, #FAEBC4 100%)',
          color: '#5b4416',
          border: '2px solid #C5A059',
          maxWidth: 280,
        }}
        data-testid="push-2fa-number"
      >
        {numberCode}
      </div>

      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        <span>Warte auf Bestätigung… {mins}:{String(secs).padStart(2, '0')}</span>
      </div>

      {recoveryCodeMode && (
        <div className="pt-4 border-t border-gray-200">
          {!showRecovery ? (
            <button
              type="button"
              onClick={() => setShowRecovery(true)}
              className="text-sm text-[#C5A059] hover:underline"
              data-testid="push-2fa-use-recovery"
            >
              Kein Zugriff auf die App? Wiederherstellungscode verwenden
            </button>
          ) : (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">Wiederherstellungscode</label>
              <input
                type="text"
                className="input-field text-center uppercase tracking-widest font-mono"
                placeholder="XXXX-XXXX"
                value={recovery}
                onChange={(e) => setRecovery(e.target.value.toUpperCase())}
                data-testid="push-2fa-recovery-input"
                maxLength={9}
              />
              <button
                type="button"
                onClick={() => onRecoveryCode?.(recovery.trim())}
                disabled={recovery.replace('-', '').length < 8}
                className="btn-primary w-full"
                data-testid="push-2fa-recovery-submit"
              >
                Anmelden mit Code
              </button>
            </div>
          )}
        </div>
      )}

      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="block mx-auto text-xs text-gray-400 hover:text-gray-600"
          data-testid="push-2fa-cancel"
        >
          Abbrechen
        </button>
      )}
    </div>
  );
}
